import type { Reaction, User } from "@/chat/protocol"
import { cn } from "@/lib/utils"

/**
 * The reactions under a message: one chip per emoji with its count, yours
 * outlined; a tap on a chip adds yours or takes it back. Picking a new one
 * happens over the message, not here — the hover bar or the long-press menu
 * (`message-actions.tsx`) — so a message nobody reacted to has nothing below.
 */
export function MessageReactions({
  reactions,
  selfId,
  participants,
  onToggle,
}: {
  reactions: Reaction[]
  selfId: string | null
  /** For naming who reacted, in the chip's tooltip. */
  participants: User[]
  onToggle: (emoji: string) => void
}) {
  function namesOf(userIds: string[]): string {
    return userIds
      .map((id) =>
        id === selfId
          ? "Вы"
          : (participants.find((p) => p.id === id)?.name ?? "…")
      )
      .join(", ")
  }

  if (reactions.length === 0) return null

  return (
    <div
      data-slot="message-reactions"
      className="flex max-w-full flex-wrap items-center gap-1 group-data-[align=end]/message:flex-row-reverse"
    >
      {reactions.map((reaction) => {
        const mine = selfId !== null && reaction.userIds.includes(selfId)
        return (
          <button
            key={reaction.emoji}
            type="button"
            onClick={() => onToggle(reaction.emoji)}
            aria-pressed={mine}
            aria-label={`${reaction.emoji} ${reaction.userIds.length}`}
            title={namesOf(reaction.userIds)}
            className={cn(
              "inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs leading-none transition-colors outline-none select-none focus-visible:ring-2 focus-visible:ring-ring",
              mine
                ? "border-primary bg-accent"
                : "border-transparent bg-muted hover:bg-accent"
            )}
          >
            <span className="text-sm">{reaction.emoji}</span>
            <span className="text-muted-foreground tabular-nums">
              {reaction.userIds.length}
            </span>
          </button>
        )
      })}
    </div>
  )
}
